import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import OverlayLoading from '../../../../../../Components/OverlayLoading';
import Form from '../../../../../../Components/Form/Form';
import InputField from '../../../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import withForm from '../../../../../../Components/Form/withForm';
import { NCZIContactPerson } from '../../../../Interfaces/ncziPatientInformations';
import { NCZI_OID } from '../../../../Enums/ncziOid.enum';
import {
	NCZIReferenceSelectItem,
	NCZIReferenceValidator,
} from '../../../ReferenceSearchSelect/NCZIReferenceSearchSelect';
import NCZIReferenceSelectField from '../../../ReferenceSelect/NCZIReferenceSelectField';
import { WritePatientSummaryContactInformation } from '../../../../Commands/WritePatientSummaryContactInformation';
import NCZICommandResult from '../../../NCZICommandResult/NCZICommandResult';
import useNCZICommandProcess from '../../../../Hooks/useNCZICommandProcess';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';

export interface NCZIEditContactPersonFormValues {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	note: string;
	type: NCZIReferenceSelectItem;
}

interface Props {
	onClose?: (needReload: boolean) => void;
	contactPerson?: NCZIContactPerson;
	patientIdentifier: string;
}

const NCZIEditContactPersonForm: React.FC<Props> = props => {
	const { onClose, patientIdentifier, contactPerson } = props;
	const [defaultValues, setDefaultValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => onClose?.(true),
		});

	const handleSubmit = async (values: NCZIEditContactPersonFormValues) => {
		const finalValues = {
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			phone: values.phone,
			note: values.note,
			type: values.type.value,
		};

		const writeCommand = WritePatientSummaryContactInformation({
			patientCode: patientIdentifier,
			contactPersons: [
				{
					...finalValues,
					recordId: contactPerson ? contactPerson.rcId : undefined,
				},
			],
		});

		await processCommand(writeCommand);
	};

	const validationSchema = yup.object({
		firstName: yup.string().required('base.shared.firstName'),
		lastName: yup.string().required('base.shared.lastName'),
		email: yup.string().nullable(),
		phone: yup.string().nullable(),
		note: yup.string().max(4000, 'nczi.validation.max4000').nullable(),
		type: NCZIReferenceValidator('nczi.contactPerson.relationship'),
	});

	useEffect(() => {
		setDefaultValues({
			firstName: contactPerson?.firstName,
			lastName: contactPerson?.lastName,
			email: contactPerson?.email,
			phone: contactPerson?.phone,
			note: contactPerson?.note,
			type: contactPerson?.type
				? {
						value: contactPerson.type,
						label: contactPerson.type?.displayName,
				  }
				: undefined,
		});
		setIsLoading(false);
	}, []);

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<div>
			{isLoading && <OverlayLoading />}
			{!isLoading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<div className='row align-items-end'>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.firstName'
									name='firstName'
									isRequired
								/>
							</InputWrapper>
						</div>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.lastName'
									name='lastName'
									isRequired
								/>
							</InputWrapper>
						</div>
					</div>
					<div className='row align-items-end'>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.email'
									name='email'
									isRequired={false}
								/>
							</InputWrapper>
						</div>
						<div className='col-md-6'>
							<InputWrapper>
								<InputField
									label='base.shared.phone'
									name='phone'
									isRequired={false}
								/>
							</InputWrapper>
						</div>
					</div>
					<InputWrapper>
						<InputField
							label='base.internalNote'
							name='note'
							isRequired={false}
							charactersLimit={4000}
						/>
					</InputWrapper>
					<NCZIReferenceSelectField
						name='type'
						oid={NCZI_OID.RELATIONSHIP}
						label='nczi.contactPerson.relationship'
						isRequired
					/>
					<div className='mt-4'>
						<SubmitButton
							title='component.nczi.setup.save'
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			)}
		</div>
	);
};

export default withForm(NCZIEditContactPersonForm);
